import { createSelector } from 'reselect'
import {
	selectMaritalStatus,
	selectPatientEmployment,
	selectPatientID,
	selectPatientZipCode,
} from './coachSystemData/selectors'
import {
	selectPatientBirthdate,
	selectPatientHeight,
	selectPatientLanguages,
	selectPatientUnits,
	selectPatientWeight,
} from './darioSystemData/selectors'
import { RootState } from '../../index'
import { PatientDashboardData } from '../../../types/patients'
import {
	CoachSystemData,
	CoachSystemDataTierCoach,
	coachTires,
} from '../../../types/coachSystemData'
import { DarioSystemData } from '../../../types/darioSystemData'
import { preciseRound } from '../../../helpers'
import {
	MyOrganizationGeneralInfo,
	MyOrganizationGeneralInfoAddress,
	MyOrganizationGeneralInfoDescription,
} from '../../../types/user'
import { selectMaritalStatuses } from '../systemConfig/selectors'

export const selectPersonalDataFirstBlock = createSelector(
	[
		selectPatientID,
		selectPatientBirthdate,
		selectPatientZipCode,
		selectPatientLanguages,
		selectPatientEmployment,
		selectMaritalStatus,
		selectMaritalStatuses,
	],
	(
		ID,
		birthdate,
		zipCode,
		languages,
		employment,
		maritalStatus,
		maritalStatusesCodes
	) => ({
		ID,
		birthdate,
		zipCode,
		languages,
		employment,
		maritalStatus:
			maritalStatusesCodes.find(item => item.id === maritalStatus)
				?.ui_text || '',
	})
)

export const selectPatientHeightMetricOrImperial = createSelector(
	[selectPatientHeight, selectPatientUnits],
	(height, units) => {
		const heightFloat = height ? parseFloat(height.toString()) : null

		if (units === 'imperial' && heightFloat) {
			const heightFt = heightFloat * 3.2808

			const inches = Math.round(
				parseFloat(
					(12 * (heightFt - parseInt(heightFt.toString()))).toString()
				)
			)

			return `${parseInt(heightFt.toString())} ft ${inches} inch`
		}

		return heightFloat
			? `${parseInt((heightFloat * 100).toString())} cm`
			: null
	}
)

export const selectPatientWeightMetricOrImperial = createSelector(
	[selectPatientWeight, selectPatientUnits],
	(weight, units) => {
		const floatWeight = weight ? parseFloat(weight.toString()) : null

		if (units === 'imperial' && floatWeight) {
			const weightLbs = floatWeight * 2.2046

			return `${preciseRound(weightLbs, 1)} lb`
		}

		return floatWeight ? `${preciseRound(floatWeight, 1)} kg` : null
	}
)

export const selectDarioSystemAndCoachingData = (
	state: RootState
): PatientDashboardData => {
	const darioSystemData =
		state.appReducer?.patientOverview?.darioSystemData ||
		({} as DarioSystemData)
	const coachSystemData =
		state.appReducer?.patientOverview?.coachSystemData ||
		({} as CoachSystemData)

	return {
		darioSystemData,
		coachSystemData,
	}
}
export const selectOrganizationName = (state: RootState): string => {
	return state.appReducer.user?.myOrganization?.orgName || ''
}
export const selectOrganizationImage = (state: RootState): string => {
	return state.appReducer.user?.myOrganization?.imageUrl || ''
}
export const selectOrganizationSettingsDescription = (
	state: RootState
): MyOrganizationGeneralInfo => {
	const description =
		state.appReducer.user?.myOrganization?.general_info.description ||
		({} as MyOrganizationGeneralInfoDescription)
	const address =
		state.appReducer.user?.myOrganization?.general_info.address ||
		({} as MyOrganizationGeneralInfoAddress)
	return {
		description,
		address,
	}
}

export const selectTiers = createSelector(
	selectDarioSystemAndCoachingData,
	(darioCoachSystemData): CoachSystemDataTierCoach[] => {
		const { coachSystemData } = darioCoachSystemData

		if (!coachSystemData) return []

		return [
			coachSystemData[coachTires.tier1Coach],
			coachSystemData[coachTires.tier2Coach],
			coachSystemData[coachTires.tier3Coach],
		]
	}
)
